/**
 * PasswordRequirements — the on-screen password checklist (AL-470)
 *
 * Shared by the first-login (new password) and reset-password flows so both
 * show the same rules. The rules themselves come from checkPassword(), which
 * derives them from the configured policy — this component only renders them.
 */

import React from 'react';
import type { PasswordRequirement } from '../../config/passwordPolicy';

interface PasswordRequirementsProps {
	requirements: PasswordRequirement[];
}

const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({ requirements }) => {
	if (requirements.length === 0) return null;

	return (
		<div className="password-strength-rules" data-testid="password-strength-rules">
			<p className="rules-title">Password must contain:</p>
			<ul className="rules-list">
				{requirements.map((rule) => (
					<li
						key={rule.key}
						className={rule.met ? 'rule-met' : 'rule-unmet'}
						data-testid={TEST_IDS[rule.key]}
					>
						<span className="rule-icon" aria-hidden="true">
							{rule.met ? '✓' : '✗'}
						</span>
						<span className="sr-only">{rule.met ? 'Met: ' : 'Not met: '}</span>
						{rule.text}
					</li>
				))}
			</ul>
		</div>
	);
};

/** Preserved from the pre-AL-470 markup so existing tests keep working. */
const TEST_IDS: Record<PasswordRequirement['key'], string> = {
	minLength: 'rule-minlength',
	hasUppercase: 'rule-uppercase',
	hasLowercase: 'rule-lowercase',
	hasNumber: 'rule-number',
	hasSpecialChar: 'rule-special-char',
};

export default PasswordRequirements;
